"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.switchToClassicProgrammingCalendar = exports.PageBanner = exports.PageBannerElement = void 0;
const web_components_1 = require("../web-components");
const PageBannerStyles_1 = require("./PageBannerStyles");
class PageBannerElement extends HTMLElement {
    // Shadow DOM root
    shadowRoot;
    // Element references
    iconElement;
    titleElement;
    descriptionElement;
    buttonElement;
    // Action handler
    clickHandler;
    // State tracking
    hasRendered = false; // Prevent duplicate initial renders
    static get observedAttributes() {
        return [
            "title",
            "icon-name",
            "description",
            "action-text",
            "action-handler",
        ];
    }
    constructor() {
        super(); // Makes <page-banner> element a valid HTMLElement
        this.shadowRoot = (0, web_components_1.safeAttachShadow)(this, "open");
    }
    // Invoked when the custom element is appended to the document's DOM.
    connectedCallback() {
        if (!this.hasRendered) {
            this.render();
            this.hasRendered = true;
            // Setup handler after DOM ready
            this.setupHandlerFromRegistry();
        }
    }
    // Invoked when one of the custom element's attributes is added, removed, or changed.
    attributeChangedCallback(name, oldValue, newValue) {
        // Browser provides oldValue, no need to track
        if (!this.hasRendered)
            return; // Skip during construction
        if (oldValue === newValue)
            return; // No actual change
        this.render();
    }
    // Invoked when the custom element is disconnected from the document's DOM.
    disconnectedCallback() {
        this.cleanup(); // Clean up event listeners
    }
    // Public method to set click handler
    setActionHandler(handler) {
        this.clickHandler = handler;
        this.setupEventListeners();
    }
    setupHandlerFromRegistry() {
        const handlerKey = this.getAttribute("data-handler-key");
        if (!handlerKey || !window.__pageBannerHandlers)
            return;
        const handler = window.__pageBannerHandlers.get(handlerKey);
        if (handler) {
            this.setActionHandler(handler);
            window.__pageBannerHandlers.delete(handlerKey); // Cleanup to prevent memory leaks
        }
    }
    async handleClick() {
        const handlerName = this.getAttribute("action-handler");
        if (!this.clickHandler && !handlerName)
            return;
        if (this.clickHandler) {
            try {
                const result = this.clickHandler();
                if (result instanceof Promise)
                    await result;
                return;
            }
            catch (error) {
                console.error("PageBanner action failed:", error);
                this.dispatchEvent(
                // TODO: Define error event type better
                new CustomEvent("action-error", {
                    detail: { error },
                    bubbles: true,
                    composed: true,
                }));
                return;
            }
        }
        if (handlerName &&
            typeof window[handlerName] === "function") {
            try {
                const result = window[handlerName]();
                if (result instanceof Promise)
                    await result;
            }
            catch (error) {
                console.error(`PageBanner action failed (${handlerName}):`, error);
                // Dispatch error event for parent components to handle
                this.dispatchEvent(new CustomEvent("action-error", {
                    detail: { error },
                    bubbles: true,
                    composed: true,
                }));
            }
        }
        else {
            console.warn(`No valid PageBanner action handler found: ${handlerName}`);
        }
    }
    boundHandleClick;
    setupEventListeners() {
        const button = this.shadowRoot.querySelector("dc-button");
        if (!button)
            return;
        // Remove existing listener if present
        if (this.boundHandleClick) {
            button.removeEventListener("click", this.boundHandleClick);
        }
        // Bind context and add new listener
        this.boundHandleClick = this.handleClick.bind(this);
        button.addEventListener("click", this.boundHandleClick);
    }
    // Clean up event listeners to prevent memory leaks
    cleanup() {
        const button = this.shadowRoot.querySelector("dc-button");
        if (button && this.boundHandleClick) {
            button.removeEventListener("click", this.boundHandleClick);
            this.boundHandleClick = undefined;
        }
    }
    render() {
        console.log("render() : [PageBanner] ---------------");
        const title = this.getAttribute("title") || "Page Banner";
        const iconName = this.getAttribute("icon-name") || "sparkles";
        const description = this.getAttribute("description") ||
            "This is a page banner component.";
        const actionText = this.getAttribute("action-text") || "Click Me";
        // Initial render
        if (!this.iconElement) {
            // Note the Dreamcoat Banner component <dc-banner> doesn't support everything we need yet so we're creating our own structure
            this.shadowRoot.innerHTML = `
                <style>
                    dc-button::part(button) {${PageBannerStyles_1.styles.actionButton}}
                </style>
                <article class="dreamcoat PageBanner" part="PageBanner">
                    <dc-icon class="PageBanner__icon" part="icon"></dc-icon>
                    <div class="PageBanner__content" part="content">
                        <div class="PageBanner__textContent" part="textContent">
                            <h4 class="H-M PageBanner__title" part="title"></h4>
                            <p class="body-M PageBanner__description" part="description"></p>
                        </div>
                        <dc-button class="PageBanner__action" part="action" variant="subtle-primary"></dc-button>
                    </div>
                </article>
            `;
            // Cache element references
            this.iconElement =
                this.shadowRoot.querySelector(".PageBanner__icon");
            this.titleElement =
                this.shadowRoot.querySelector(".PageBanner__title");
            this.descriptionElement = this.shadowRoot.querySelector(".PageBanner__description");
            this.buttonElement = this.shadowRoot.querySelector(".PageBanner__action");
            // Setup listeners once
            this.setupEventListeners();
        }
        // Update only changed attributes (minimal DOM writes)
        if (this.iconElement.getAttribute("name") !== iconName) {
            this.iconElement.setAttribute("name", iconName);
            this.iconElement.setAttribute("family", "solid");
        }
        if (this.titleElement.textContent !== title) {
            this.titleElement.textContent = title;
        }
        if (this.descriptionElement.textContent !== description) {
            this.descriptionElement.textContent = description;
        }
        if (this.buttonElement.getAttribute("label") !== actionText) {
            this.buttonElement.setAttribute("label", actionText);
        }
    }
}
exports.PageBannerElement = PageBannerElement;
// Wrapper function to create PageBanner web component markup from props
const PageBanner = (props) => {
    const { title, iconName, description, actionText, elementId } = props;
    const id = elementId || `page-banner-${Date.now()}`; // Unique ID for the element instance
    // Avoid global state, use data attributes
    const actionHandlerKey = props.actionHandler
        ? `__pageBanner_${id}`
        : undefined;
    // Store handler in a scoped registry
    if (props.actionHandler && actionHandlerKey) {
        // Minimize globals, use explicit pattern
        if (!window.__pageBannerHandlers)
            window.__pageBannerHandlers = new Map();
        window.__pageBannerHandlers.set(actionHandlerKey, props.actionHandler);
    }
    const html = `<page-banner 
        id="${id}"
        title="${title || "Page Banner"}" 
        icon-name="${iconName || "sparkles"}" 
        description="${description || "This is a page banner component."}" 
        action-text="${actionText || "Click Me"}"
        ${actionHandlerKey ? `data-handler-key="${actionHandlerKey}"` : ""}>
    </page-banner>`;
    return html;
};
exports.PageBanner = PageBanner;
const switchToClassicProgrammingCalendar = async (athleteId, affiliateId) => {
    try {
        const URL = `/workouts/calendar/new-calendar-preference`;
        const data = {
            athleteId,
            affiliateId,
            USE_NEW_PROGRAMMING_CALENDAR: false,
        };
        const message = "Switching to classic programming calendar...";
        const successFn = (result) => {
            if (typeof window.pendo !== "undefined" && result.pendoEventData) {
                try {
                    window.pendo.track(result.pendoEventData.event, result.pendoEventData);
                }
                catch (error) {
                    console.error("[PageBanner] Pendo tracking failed:", error);
                }
            }
            else {
                console.warn("[PageBanner] Pendo not available for tracking");
            }
            window.location.reload();
        };
        const errorFunction = () => {
            window.showFlashError?.("There was an error switching to the classic calendar. Please try again later.");
        };
        if (typeof window.postAsyncJSON === "function") {
            window.postAsyncJSON(URL, data, message, successFn, errorFunction);
        }
        else {
            console.warn("postAsyncJSON not available");
            errorFunction();
        }
    }
    catch (error) {
        console.error("Failed to switch to classic calendar:", error);
        window.showFlashError?.("There was an error switching to the classic calendar. Please try again later.");
    }
};
exports.switchToClassicProgrammingCalendar = switchToClassicProgrammingCalendar;
// Register the web component
(0, web_components_1.registerWebComponent)("page-banner", PageBannerElement);
//# sourceMappingURL=PageBanner.js.map